import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import { logError, logInfo, logWarn } from '../config_and_helpers/logger';
import {
  formatUploadFinalizeErrors,
  notifyUploadFinalizeFailure,
  uploadFinalizeErrorMessage,
} from '../utils/uploadFinalizeErrors';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'worker_timesheet_client';

/**
 * Maps common MIME types to a single file extension when the original filename
 * has no extension (helps backend S3 keys and Content-Type align).
 *
 * @type {Readonly<Record<string, string>>}
 */
const WORKER_TIMESHEET_MIME_TO_EXT = Object.freeze({
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
});

/**
 * Normalizes an upload manifest filename: strips path segments to basename; if the
 * basename has no extension but `content_type` maps to a known extension, appends it
 * and logs a warning (traceability for odd uploads).
 *
 * @param {string} rawName - Original file name (may include path separators).
 * @param {string} [contentType] - MIME type (falls back to octet-stream when empty).
 * @param {'attachment'|'expense_receipt'|'sick_note'} context - Log context label.
 * @returns {{ filename: string, content_type: string }}
 */
function sanitizeWorkerTimesheetUploadManifestEntry(
  rawName,
  contentType,
  context,
) {
  const base =
    String(rawName || '')
      .split(/[/\\]/)
      .pop()
      .trim() || 'upload';
  const ctRaw =
    String(contentType || '').trim() || 'application/octet-stream';
  const ct = ctRaw.toLowerCase();
  const hasExt = /\.[a-z0-9]{1,8}$/i.test(base);
  if (hasExt) {
    return { filename: base, content_type: ctRaw };
  }
  const ext = WORKER_TIMESHEET_MIME_TO_EXT[ct];
  if (ext) {
    logWarn(LOG_CONTEXT, 'inferred file extension from MIME (filename had no extension)', {
      context,
      rawName: base,
      contentType: ctRaw,
    });
    return { filename: `${base}${ext}`, content_type: ctRaw };
  }
  return { filename: base, content_type: ctRaw };
}

/**
 * Applies {@link sanitizeWorkerTimesheetUploadManifestEntry} to each expense row
 * that carries receipt metadata for the presigned flow.
 *
 * @param {unknown[]} expenses - `payload.expenses` from the timesheet form.
 * @returns {unknown[]}
 */
function mapExpensesWithSanitizedReceiptFilenames(expenses) {
  if (!Array.isArray(expenses)) return expenses;
  return expenses.map(row => {
    if (!row || !row.has_receipt || !row.receipt_filename) {
      return row;
    }
    const { filename, content_type } = sanitizeWorkerTimesheetUploadManifestEntry(
      row.receipt_filename,
      row.receipt_content_type || 'application/octet-stream',
      'expense_receipt',
    );
    return {
      ...row,
      receipt_filename: filename,
      receipt_content_type: content_type,
    };
  });
}

/**
 * API path for persisting a worker's monthly timesheet (no auth).
 * @type {string}
 */
export const WORKER_TIMESHEET_PATH = 'receive_worker_timesheet/';

/**
 * Authenticated list + detail for the internal worker timesheet dashboard.
 * @type {string}
 */
export const WORKER_TIMESHEETS_DASH_PATH = 'worker-timesheets/';

/**
 * Maps UI sort state to DRF `ordering` query value.
 *
 * @param {string} sortBy - `created_at` | `month` | `id`
 * @param {string} sortDirection - `asc` | `desc`
 * @returns {string}
 */
const TIMESHEET_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Normalizes API `month` (YYYY-MM or ISO date string) to YYYY-MM.
 *
 * @param {*} value
 * @returns {string|null}
 */
export function normalizeTimesheetMonth(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const yyyyMm = raw.length >= 7 ? raw.slice(0, 7) : raw;
  return TIMESHEET_MONTH_RE.test(yyyyMm) ? yyyyMm : null;
}

export function getWorkerTimesheetsOrderingParam(sortBy, sortDirection) {
  const field =
    sortBy === 'month' || sortBy === 'id' || sortBy === 'created_at'
      ? sortBy
      : 'created_at';
  const prefix = sortDirection === 'desc' ? '-' : '';
  return `${prefix}${field}`;
}

/**
 * Fetches a paginated list of worker timesheets (internal users; token required).
 * Mirrors `getOrders` append/reset and `next` URL handling.
 *
 * @async
 * @param {string} token
 * @param {function} setList
 * @param {Object} [options]
 * @param {string} [options.searchInput]
 * @param {string|null} [options.nextPage] - Full URL for next page from `response.data.next`
 * @param {string} [options.monthFilter] - `YYYY-MM` or empty
 * @param {string} [options.sortBy] - `created_at` | `month` | `id`
 * @param {string} [options.sortDirection] - `asc` | `desc`
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ success?: boolean, canceled?: boolean, nextPage?: string, reachedEnd?: boolean, error?: * }>}
 */
export const getWorkerTimesheets = async (token, setList, options = {}) => {
  const {
    searchInput = '',
    nextPage = null,
    monthFilter = '',
    sortBy = 'created_at',
    sortDirection = 'desc',
    signal = undefined,
  } = options;

  const ordering = getWorkerTimesheetsOrderingParam(sortBy, sortDirection);
  let url = nextPage
    ? nextPage
    : `${BACKEND_URL}${WORKER_TIMESHEETS_DASH_PATH}?page_num=1`;

  if (!nextPage) {
    if (searchInput) {
      url += `&search=${encodeURIComponent(searchInput)}`;
    }
    const monthTrim = String(monthFilter || '').trim();
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthTrim)) {
      url += `&month=${encodeURIComponent(monthTrim)}`;
    }
    url += `&ordering=${encodeURIComponent(ordering)}`;
  }

  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
      signal,
    });

    const nextCursor = response.data.next;

    if (!nextCursor) {
      if (nextPage) {
        setList(prev => {
          const prevList = Array.isArray(prev) ? prev : [];
          const incoming = Array.isArray(response.data.results)
            ? response.data.results
            : [];
          const seenIds = new Set(prevList.map(r => r?.id));
          const uniqueIncoming = incoming.filter(
            row => row?.id == null || !seenIds.has(row.id),
          );
          return [...prevList, ...uniqueIncoming];
        });
      } else {
        setList(response.data.results || []);
      }
      return { success: true, reachedEnd: true };
    }

    if (!nextPage) {
      setList(response.data.results || []);
    } else {
      setList(prev => {
        const prevList = Array.isArray(prev) ? prev : [];
        const incoming = Array.isArray(response.data.results)
          ? response.data.results
          : [];
        const seenIds = new Set(prevList.map(r => r?.id));
        const uniqueIncoming = incoming.filter(
          row => row?.id == null || !seenIds.has(row.id),
        );
        return [...prevList, ...uniqueIncoming];
      });
    }

    return { success: true, nextPage: response.data.next };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    logError(LOG_CONTEXT, 'getWorkerTimesheets failed', error.response?.data ?? {
      message: error.message,
    });
    return { success: false, error: error.response?.data ?? error.message };
  }
};

/**
 * Collects distinct report months (YYYY-MM) from all worker timesheet list pages.
 *
 * @async
 * @param {string} token
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<
 *   | { success: true, months: string[] }
 *   | { success: false, error?: * }
 *   | { canceled: true }
 * >}
 */
export const fetchWorkerTimesheetReportMonths = async (token, options = {}) => {
  const { signal } = options;
  const ordering = getWorkerTimesheetsOrderingParam('month', 'desc');
  let url = `${BACKEND_URL}${WORKER_TIMESHEETS_DASH_PATH}?page_num=1&ordering=${encodeURIComponent(ordering)}`;
  const monthSet = new Set();

  logInfo(LOG_CONTEXT, 'fetchWorkerTimesheetReportMonths start', { url });

  try {
    while (url) {
      const response = await axios.get(url, {
        headers: { Authorization: `Token ${token}` },
        signal,
      });
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : [];
      results.forEach(row => {
        const normalized = normalizeTimesheetMonth(row?.month);
        if (normalized) monthSet.add(normalized);
      });
      url = response.data?.next || null;
    }

    const months = Array.from(monthSet).sort((a, b) => b.localeCompare(a));
    logInfo(LOG_CONTEXT, 'fetchWorkerTimesheetReportMonths success', {
      monthCount: months.length,
    });
    return { success: true, months };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    logError(LOG_CONTEXT, 'fetchWorkerTimesheetReportMonths failed', {
      message: error.message,
      detail: error.response?.data,
    });
    return { success: false, error: error.response?.data ?? error.message };
  }
};

/**
 * Fetches a single timesheet with nested daily_hours, expenses, attachments (presigned URLs).
 *
 * @async
 * @param {string} token
 * @param {number|string} id
 * @param {AbortSignal} [signal]
 * @returns {Promise<
 *   | { success: true, data: object }
 *   | { success: false, status?: number, detail?: string }
 * >}
 */
export const getWorkerTimesheetDetail = async (token, id, signal) => {
  const url = `${BACKEND_URL}${WORKER_TIMESHEETS_DASH_PATH}${id}/`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
      signal,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const status = error.response?.status;
    const detail =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message;
    logError(LOG_CONTEXT, 'getWorkerTimesheetDetail failed', {
      status,
      detail,
      id,
    });
    return { success: false, status, detail };
  }
};

/**
 * Deletes a worker timesheet by id (internal users; token required).
 * Expects backend `DELETE /v1/worker-timesheets/{id}/` returning 204 on success.
 *
 * @async
 * @param {string} token
 * @param {number|string} id
 * @returns {Promise<
 *   | { success: true, toast: function }
 *   | { success: false, status?: number, detail?: string }
 * >}
 */
export const deleteWorkerTimesheet = async (token, id) => {
  const url = `${BACKEND_URL}${WORKER_TIMESHEETS_DASH_PATH}${id}/`;
  try {
    await axios.delete(url, {
      headers: { Authorization: `Token ${token}` },
    });
    logInfo(LOG_CONTEXT, 'deleteWorkerTimesheet success', { id });
    return {
      success: true,
      toast: () =>
        showToast('דיווח העובד נמחק בהצלחה', 'success', 'top-center', 3000),
    };
  } catch (error) {
    const status = error.response?.status;
    const detail =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message;
    logError(LOG_CONTEXT, 'deleteWorkerTimesheet failed', {
      status,
      detail,
      id,
    });
    return { success: false, status, detail };
  }
};

/**
 * Path segments for the export endpoints (mirroring `WORKER_TIMESHEETS_DASH_PATH`).
 * @type {string}
 */
export const WORKER_TIMESHEETS_EXPORT_LIST_EXCEL_PATH = 'worker-timesheets/export/excel/';
export const WORKER_TIMESHEETS_EXPORT_LIST_PDF_PATH = 'worker-timesheets/export/pdf/';
export const WORKER_TIMESHEET_EXPORT_DETAIL_EXCEL_SUFFIX = 'export/excel/';
export const WORKER_TIMESHEET_EXPORT_DETAIL_PDF_SUFFIX = 'export/pdf/';

/**
 * Builds list-export query string from current UI filters.
 *
 * @param {Object} options
 * @param {string} [options.searchInput]
 * @param {string} [options.monthFilter] - `YYYY-MM` or empty
 * @param {string} [options.sortBy]
 * @param {string} [options.sortDirection]
 * @returns {string} - Query string starting with `?` or empty string when no params.
 */
function buildListExportQuery({
  searchInput = '',
  monthFilter = '',
  sortBy = 'created_at',
  sortDirection = 'desc',
} = {}) {
  const params = new URLSearchParams();
  if (searchInput) {
    params.set('search', searchInput);
  }
  const monthTrim = String(monthFilter || '').trim();
  if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthTrim)) {
    params.set('month', monthTrim);
  }
  params.set('ordering', getWorkerTimesheetsOrderingParam(sortBy, sortDirection));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Extracts a filename from `Content-Disposition` (RFC 5987 + plain forms).
 * Falls back to `defaultName` when the header is missing or unparsable.
 *
 * @param {string|undefined} header - Raw `Content-Disposition` header value.
 * @param {string} defaultName - Fallback filename (must include extension).
 * @returns {string}
 */
function parseFilenameFromContentDisposition(header, defaultName) {
  if (!header || typeof header !== 'string') return defaultName;
  const utf8Match = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through to plain filename match
    }
  }
  const plainMatch = header.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  if (plainMatch) {
    const value = (plainMatch[1] || plainMatch[2] || '').trim();
    if (value) return value;
  }
  return defaultName;
}

/**
 * Triggers a browser download for an in-memory blob via a temporary `<a>` element.
 * Mirrors the standard browser blob-download pattern used across client modules.
 *
 * @param {Blob} blob
 * @param {string} filename
 * @returns {void}
 */
function triggerBlobDownload(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Internal: GETs a binary export and triggers a browser download.
 *
 * @async
 * @param {string} url
 * @param {string} token
 * @param {string} defaultFilename
 * @param {AbortSignal} [signal]
 * @param {string} logLabel - Human label for log entries.
 * @returns {Promise<
 *   | { success: true, filename: string }
 *   | { success: false, status?: number, detail?: string }
 *   | { canceled: true }
 * >}
 */
async function downloadExportBlob(url, token, defaultFilename, signal, logLabel) {
  try {
    logInfo(LOG_CONTEXT, `${logLabel} start`, { url });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
      responseType: 'blob',
      signal,
    });
    const contentType =
      response.headers?.['content-type'] || 'application/octet-stream';
    const filename = parseFilenameFromContentDisposition(
      response.headers?.['content-disposition'],
      defaultFilename,
    );
    const blob = new Blob([response.data], { type: contentType });
    triggerBlobDownload(blob, filename);
    logInfo(LOG_CONTEXT, `${logLabel} success`, { filename, contentType });
    return { success: true, filename };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    /** Blob error responses arrive as Blob bodies; surface status + best-effort detail. */
    const status = error.response?.status;
    const detail = error.response?.statusText || error.message;
    logError(LOG_CONTEXT, `${logLabel} failed`, { status, detail, url });
    return { success: false, status, detail };
  }
}

/**
 * Downloads an Excel (.xlsx) export of worker timesheets.
 * Monthly summary modal passes only `monthFilter` (no list search/sort).
 *
 * @async
 * @param {string} token
 * @param {Object} [options] - Filter shape shared with {@link getWorkerTimesheets}.
 * @param {string} [options.searchInput]
 * @param {string} [options.monthFilter] - `YYYY-MM` for full monthly report
 * @param {string} [options.sortBy]
 * @param {string} [options.sortDirection]
 * @param {AbortSignal} [options.signal]
 * @returns {ReturnType<typeof downloadExportBlob>}
 */
export const exportWorkerTimesheetsExcel = async (token, options = {}) => {
  const { signal, ...filters } = options;
  const url = `${BACKEND_URL}${WORKER_TIMESHEETS_EXPORT_LIST_EXCEL_PATH}${buildListExportQuery(filters)}`;
  const fallback = `worker-timesheets-${new Date().toISOString().slice(0, 10)}.xlsx`;
  return downloadExportBlob(url, token, fallback, signal, 'exportWorkerTimesheetsExcel');
};

/**
 * Downloads a PDF export of the currently filtered worker-timesheets list.
 *
 * @async
 * @param {string} token
 * @param {Object} [options] - Same filter shape as {@link getWorkerTimesheets}.
 * @param {string} [options.searchInput]
 * @param {string} [options.monthFilter]
 * @param {string} [options.sortBy]
 * @param {string} [options.sortDirection]
 * @param {AbortSignal} [options.signal]
 * @returns {ReturnType<typeof downloadExportBlob>}
 */
export const exportWorkerTimesheetsPdf = async (token, options = {}) => {
  const { signal, ...filters } = options;
  const url = `${BACKEND_URL}${WORKER_TIMESHEETS_EXPORT_LIST_PDF_PATH}${buildListExportQuery(filters)}`;
  const fallback = `worker-timesheets-${new Date().toISOString().slice(0, 10)}.pdf`;
  return downloadExportBlob(url, token, fallback, signal, 'exportWorkerTimesheetsPdf');
};

/**
 * Downloads an Excel (.xlsx) export of a single timesheet.
 *
 * @async
 * @param {string} token
 * @param {number|string} id
 * @param {AbortSignal} [signal]
 * @returns {ReturnType<typeof downloadExportBlob>}
 */
export const exportWorkerTimesheetExcel = async (token, id, signal) => {
  const url = `${BACKEND_URL}${WORKER_TIMESHEETS_DASH_PATH}${id}/${WORKER_TIMESHEET_EXPORT_DETAIL_EXCEL_SUFFIX}`;
  const fallback = `worker-timesheet-${id}.xlsx`;
  return downloadExportBlob(url, token, fallback, signal, 'exportWorkerTimesheetExcel');
};

/**
 * Downloads a PDF export of a single timesheet.
 *
 * @async
 * @param {string} token
 * @param {number|string} id
 * @param {AbortSignal} [signal]
 * @returns {ReturnType<typeof downloadExportBlob>}
 */
export const exportWorkerTimesheetPdf = async (token, id, signal) => {
  const url = `${BACKEND_URL}${WORKER_TIMESHEETS_DASH_PATH}${id}/${WORKER_TIMESHEET_EXPORT_DETAIL_PDF_SUFFIX}`;
  const fallback = `worker-timesheet-${id}.pdf`;
  return downloadExportBlob(url, token, fallback, signal, 'exportWorkerTimesheetPdf');
};

/**
 * Public path for finalizing S3 upload statuses after presigned POSTs.
 * @type {string}
 */
export const WORKER_TIMESHEET_FINALIZE_UPLOAD_PATH =
  'worker_timesheet_update_upload_status/';

/**
 * Stable id for matching presigned URL rows to local file rows (mirrors `frontend_id` in order/quote clients).
 *
 * @returns {string}
 */
export function generateWorkerTimesheetFrontendId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `fe_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * True when the submission includes files that must go through S3 presigned POSTs.
 *
 * @param {Object} options
 * @param {Array<{ file: File, frontend_id: string }>} [options.attachmentsWithIds]
 * @param {Array<{ file: File, frontend_id: string }>} [options.expenseReceiptsWithIds]
 * @param {Array<{ file: File, frontend_id: string }>} [options.sickNotesWithIds]
 * @returns {boolean}
 */
function hasPresignedUploadFiles(options = {}) {
  const nAtt = Array.isArray(options.attachmentsWithIds)
    ? options.attachmentsWithIds.length
    : 0;
  const nExp = Array.isArray(options.expenseReceiptsWithIds)
    ? options.expenseReceiptsWithIds.length
    : 0;
  const nSick = Array.isArray(options.sickNotesWithIds)
    ? options.sickNotesWithIds.length
    : 0;
  return nAtt > 0 || nExp > 0 || nSick > 0;
}

/**
 * POST a single file to S3 using a presigned POST policy (same shape as `quote_client.uploadQuoteFileToS3`).
 *
 * @async
 * @param {{ url: string, fields?: Object }} presignedPostData
 * @param {File} file
 * @returns {Promise<boolean>}
 */
async function postFileToS3Presigned(presignedPostData, file) {
  const formData = new FormData();
  const fields = presignedPostData.fields || {};
  Object.keys(fields).forEach(key => {
    formData.append(key, fields[key]);
  });
  formData.append('file', file);
  try {
    const response = await axios.post(presignedPostData.url, formData);
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    logError(LOG_CONTEXT, 'postFileToS3Presigned failed', {
      message: error.message,
      urlHost:
        typeof presignedPostData.url === 'string'
          ? presignedPostData.url.slice(0, 48)
          : undefined,
    });
    return false;
  }
}

/**
 * Uploads multiple files to S3; matches `presignedList` entries to `filesWithIds` by `frontend_id`.
 *
 * @async
 * @param {Array<{ frontend_id: string, url: string, fields?: Object }>} presignedList
 * @param {Array<{ frontend_id: string, file: File }>} filesWithIds
 * @returns {Promise<Record<string, 'completed'|'failed'>>}
 */
async function uploadWorkerTimesheetFilesToS3(presignedList, filesWithIds) {
  /** @type {Record<string, 'completed'|'failed'>} */
  const statuses = {};
  await Promise.all(
    (presignedList || []).map(async presigned => {
      const fid = presigned.frontend_id;
      const item = filesWithIds.find(f => f.frontend_id === fid);
      if (!item?.file) {
        statuses[fid] = 'failed';
        return;
      }
      const ok = await postFileToS3Presigned(presigned, item.file);
      statuses[fid] = ok ? 'completed' : 'failed';
    }),
  );
  for (const { frontend_id } of filesWithIds) {
    if (statuses[frontend_id] === undefined) {
      statuses[frontend_id] = 'failed';
    }
  }
  return statuses;
}

/**
 * Notifies the backend that S3 uploads finished (mirrors `finalizeOrderImageUploadStatus`).
 *
 * @async
 * @param {number|string} timesheetId
 * @param {Object} body
 * @param {Record<string, 'completed'|'failed'>} body.attachment_upload_status
 * @param {Record<string, 'completed'|'failed'>} body.expense_receipt_upload_status
 * @param {Record<string, 'completed'|'failed'>} body.sick_note_upload_status
 * @param {string} [body.finalize_token] - Short-lived token from phase 1 when backend requires it.
 * @returns {Promise<{ success: true } | { success: false, detail?: string }>}
 */
async function finalizeWorkerTimesheetUploadStatus(timesheetId, body) {
  try {
    await axios.post(
      `${BACKEND_URL}${WORKER_TIMESHEET_FINALIZE_UPLOAD_PATH}`,
      { timesheet_id: timesheetId, ...body },
      {
        headers: { 'Content-Type': 'application/json' },
        validateStatus: status => status >= 200 && status < 300,
      },
    );
    return { success: true };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    const messages = formatUploadFinalizeErrors(errPayload);
    logError(LOG_CONTEXT, 'finalizeWorkerTimesheetUploadStatus failed', {
      timesheetId,
      status: error.response?.status,
    });
    notifyUploadFinalizeFailure(messages);
    return {
      success: false,
      detail: uploadFinalizeErrorMessage(errPayload),
      messages,
    };
  }
}

/**
 * Builds the success return shape shared by JSON-only and S3 flows.
 *
 * @param {Object} data - Phase-1 response body (`status`, `id`, `month`, …).
 * @returns {{ success: true, data: object, toast: function }}
 */
function buildSubmitWorkerTimesheetSuccess(data) {
  const id = data.id;
  return {
    success: true,
    data,
    toast: () =>
      showToast(
        `הדיווח נשלח בהצלחה. מזהה אסמכתא: ${id}`,
        'success',
        'top-left',
        5000,
      ),
  };
}

/**
 * Submits a worker monthly timesheet.
 * - **No files:** single `POST` with JSON (legacy).
 * - **With files:** phase 1 JSON metadata + `attachment_files`, receive presigned URLs, POST each file to S3, then finalize upload status on the backend.
 *
 * @async
 * @param {Object} payload - JSON-serializable body; each expense must include `frontend_id` and `has_receipt` when using file uploads.
 * @param {Object} [options]
 * @param {Array<{ file: File, frontend_id: string }>} [options.attachmentsWithIds] - Generic attachments only (rows with a file).
 * @param {Array<{ file: File, frontend_id: string }>} [options.expenseReceiptsWithIds] - Expense receipts only.
 * @param {Array<{ file: File, frontend_id: string }>} [options.sickNotesWithIds] - Sick-day doctor/clinic notes.
 * @returns {Promise<
 *   | { success: true, data: object, toast: function }
 *   | { success: false, detail: string }
 * >}
 */
export const submitWorkerTimesheet = async (payload, options = {}) => {
  const attachmentsWithIds = options.attachmentsWithIds || [];
  const expenseReceiptsWithIds = options.expenseReceiptsWithIds || [];
  const sickNotesWithIds = options.sickNotesWithIds || [];
  const usePresignedFlow = hasPresignedUploadFiles(options);

  logInfo(LOG_CONTEXT, 'submitWorkerTimesheet start', {
    usePresignedFlow,
    expenseCount: Array.isArray(payload?.expenses) ? payload.expenses.length : 0,
    attachmentsWithIdsCount: attachmentsWithIds.length,
    expenseReceiptsWithIdsCount: expenseReceiptsWithIds.length,
    sickNotesWithIdsCount: sickNotesWithIds.length,
  });

  const url = `${BACKEND_URL}${WORKER_TIMESHEET_PATH}`;

  /** Receipt filenames/MIME are sanitized here so the page can pass raw `File` metadata. */
  const payloadWithSanitizedExpenses = {
    ...payload,
    expenses: mapExpensesWithSanitizedReceiptFilenames(payload.expenses),
  };

  try {
    if (!usePresignedFlow) {
      const response = await axios.post(url, payloadWithSanitizedExpenses, {
        headers: { 'Content-Type': 'application/json' },
        validateStatus: status => status === 201 || status === 400 || status === 500,
      });
      return normalizeResponse(response);
    }

    const attachment_files = attachmentsWithIds.map(a => {
      const { filename, content_type } = sanitizeWorkerTimesheetUploadManifestEntry(
        a.file.name,
        a.file.type || 'application/octet-stream',
        'attachment',
      );
      return {
        frontend_id: a.frontend_id,
        filename,
        content_type,
        size: a.file.size,
      };
    });

    const sick_note_files = sickNotesWithIds.map(s => {
      const { filename, content_type } = sanitizeWorkerTimesheetUploadManifestEntry(
        s.file.name,
        s.file.type || 'application/octet-stream',
        'sick_note',
      );
      return {
        frontend_id: s.frontend_id,
        filename,
        content_type,
        size: s.file.size,
      };
    });

    const phase1Body = {
      ...payloadWithSanitizedExpenses,
      attachment_files,
      sick_note_files,
    };

    const response = await axios.post(url, phase1Body, {
      headers: { 'Content-Type': 'application/json' },
      validateStatus: status => status === 201 || status === 400 || status === 500,
    });

    if (response.status !== 201 || response.data?.status !== 'ok') {
      return normalizeResponse(response);
    }

    const data = response.data;
    const timesheetId = data.id;
    const attachmentPresigned = data.attachment_presigned_urls || [];
    const expensePresigned = data.expense_receipt_presigned_urls || [];
    const sickNotePresigned = data.sick_note_presigned_urls || [];

    if (
      attachmentsWithIds.length > attachmentPresigned.length ||
      expenseReceiptsWithIds.length > expensePresigned.length ||
      sickNotesWithIds.length > sickNotePresigned.length
    ) {
      logError(LOG_CONTEXT, 'submitWorkerTimesheet presigned count mismatch', {
        timesheetId,
        expectedAttachments: attachmentsWithIds.length,
        gotAttachmentPresigned: attachmentPresigned.length,
        expectedReceipts: expenseReceiptsWithIds.length,
        gotReceiptPresigned: expensePresigned.length,
        expectedSickNotes: sickNotesWithIds.length,
        gotSickNotePresigned: sickNotePresigned.length,
      });
    }

    const attachment_upload_status = await uploadWorkerTimesheetFilesToS3(
      attachmentPresigned,
      attachmentsWithIds,
    );
    const expense_receipt_upload_status = await uploadWorkerTimesheetFilesToS3(
      expensePresigned,
      expenseReceiptsWithIds,
    );
    const sick_note_upload_status = await uploadWorkerTimesheetFilesToS3(
      sickNotePresigned,
      sickNotesWithIds,
    );

    const finalizeBody = {
      attachment_upload_status,
      expense_receipt_upload_status,
      sick_note_upload_status,
    };
    if (data.finalize_token) {
      finalizeBody.finalize_token = data.finalize_token;
    }

    const finalized = await finalizeWorkerTimesheetUploadStatus(
      timesheetId,
      finalizeBody,
    );
    if (!finalized.success) {
      return {
        success: false,
        detail:
          finalized.detail ||
          'שגיאה בסיום העלאת הקבצים. צרו קשר עם התמיכה.',
      };
    }

    const anyAttachmentFailed = Object.values(attachment_upload_status).some(
      s => s === 'failed',
    );
    const anyReceiptFailed = Object.values(
      expense_receipt_upload_status,
    ).some(s => s === 'failed');
    const anySickNoteFailed = Object.values(sick_note_upload_status).some(
      s => s === 'failed',
    );

    if (anyAttachmentFailed || anyReceiptFailed || anySickNoteFailed) {
      return {
        success: false,
        detail:
          'חלק מהקבצים לא הועלו בהצלחה. בדקו את החיבור ונסו שוב, או צרו קשר עם התמיכה.',
      };
    }

    return buildSubmitWorkerTimesheetSuccess(data);
  } catch (error) {
    logError(LOG_CONTEXT, 'submitWorkerTimesheet failed', error.response?.data ?? {
      message: error.message,
    });
    return {
      success: false,
      detail:
        error.response?.data?.detail ||
        'שגיאת רשת. בדקו את החיבור ונסו שוב.',
    };
  }
};

/**
 * Maps axios response to success or error shape.
 *
 * @param {import('axios').AxiosResponse} response
 * @returns {{ success: true, data: object, toast: function } | { success: false, detail: string }}
 */
function normalizeResponse(response) {
  const data = response.data;
  if (response.status === 201 && data?.status === 'ok') {
    return buildSubmitWorkerTimesheetSuccess(data);
  }
  if (response.status === 400 && data?.detail) {
    return { success: false, detail: String(data.detail) };
  }
  logError(LOG_CONTEXT, 'submitWorkerTimesheet unexpected response', {
    status: response.status,
    data,
  });
  return {
    success: false,
    detail:
      data?.detail ||
      'שגיאת שרת. נסו שוב או צרו קשר עם התמיכה.',
  };
}
